import { useRef, useState } from "react";
import { applyCheckboxChange } from "utils/exclusiveCheckboxes";
import { isFieldVisible, visibleFields } from "utils/documentFieldVisibility";
import { buildFieldBlocks } from "utils/adminFieldGroups";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";

const AdminFillFieldsForm = ({
  fields = [],
  values,
  onChange,
  disabled = false,
  visibilityContext = {},
  assetUrls = {},
  uppercase = false,
}) => {
  const [signatureField, setSignatureField] = useState("");
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  if (!fields.length) return null;

  const setValue = (name, value) => onChange({ ...values, [name]: value });

  // A field whose condition no longer holds must not keep a value it was given before it hid, or a
  // hidden branch would still be stamped onto the form. Shown fields are what the admin can touch.
  // visibilityContext carries autofilled controller values the admin cannot edit here (a picked
  // agent's capacity box), so a branch gated by one is unhidden without polluting what admin submits.
  const shown = visibleFields(fields, { ...visibilityContext, ...values });
  const blocks = buildFieldBlocks(shown, values);

  const handlePhoto = (name, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setValue(name, reader.result);
    reader.readAsDataURL(file);
  };

  const startDraw = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const context = canvas.getContext("2d");
    context.strokeStyle = "#0f172a";
    context.lineWidth = 2.5;
    context.lineCap = "round";
    context.beginPath();
    context.moveTo(event.clientX - rect.left, event.clientY - rect.top);
    drawingRef.current = true;
  };

  const moveDraw = (event) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const context = canvas.getContext("2d");
    context.lineTo(event.clientX - rect.left, event.clientY - rect.top);
    context.stroke();
  };

  const signaturePreview = (field, src) => (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 0.5,
        bgcolor: "background.paper",
        lineHeight: 0,
      }}
    >
      <img src={src} alt={field.label} style={{ maxHeight: 56, maxWidth: 220, display: "block" }} />
    </Box>
  );

  /**
   * A signature the MARA record owns. Preview only: the agent's signature is theirs, and Manage
   * MARA is the single place it is set or changed, so there is nothing to draw or replace here.
   * With none on file the document cannot be completed at all, which the caller states as the
   * reason the Complete button is off rather than hinting at a control that does not exist.
   */
  const renderRecordOwnedSignature = (field) => {
    const onFile = assetUrls[field.name];
    return (
      <Box>
        {onFile && signaturePreview(field, onFile)}
        <Box sx={{ fontSize: 12, color: onFile ? "text.secondary" : "error.main", mt: 0.5 }}>
          {onFile
            ? `${field.label} on file will be applied automatically.`
            : `No ${field.label.toLowerCase()} on file. Add it under Manage MARA.`}
        </Box>
      </Box>
    );
  };

  const renderSignature = (field) => {
    if (field.recordOwned) return renderRecordOwnedSignature(field);

    const value = values[field.name] || "";
    const preview = value && value.startsWith("data:") ? value : assetUrls[field.name];
    return (
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          {preview && signaturePreview(field, preview)}
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={() => setSignatureField(field.name)}
          >
            {preview ? `Replace ${field.label}` : `Sign ${field.label}`}
            {field.required ? " *" : ""}
          </Button>
        </Stack>
        <Box sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
          {preview ? `${field.label} drawn for this document.` : ""}
        </Box>
      </Box>
    );
  };

  /** One control for one field, with the label its block decided on — a titled block strips the
   * prefix its own heading already shows. */
  const renderControl = (field, label) => {
    const value = values[field.name] || "";

    if (field.fieldType === "SIGNATURE") return renderSignature(field);

    if (field.fieldType === "PHOTO") {
      return (
        <Button size="small" variant="outlined" component="label" disabled={disabled}>
          {value ? `Photo attached: ${label}` : `Upload ${label}`}
          {field.required ? " *" : ""}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => handlePhoto(field.name, event.target.files?.[0])}
          />
        </Button>
      );
    }

    if (field.fieldType === "CHECKBOX") {
      return (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={value === "true"}
              disabled={disabled}
              onChange={(event) =>
                onChange(applyCheckboxChange(fields, values, field, event.target.checked))
              }
            />
          }
          label={`${label}${field.required ? " *" : ""}`}
          slotProps={{ typography: { fontSize: 14 } }}
        />
      );
    }

    return (
      <TextField
        label={label}
        size="small"
        fullWidth
        required={field.required}
        disabled={disabled}
        type={field.fieldType === "DATE" ? "date" : "text"}
        InputLabelProps={field.fieldType === "DATE" ? { shrink: true } : undefined}
        multiline={field.fieldType === "MULTILINE"}
        minRows={field.fieldType === "MULTILINE" ? 2 : undefined}
        value={value}
        onChange={(event) => setValue(field.name, event.target.value)}
        inputProps={uppercase ? { style: { textTransform: "uppercase" } } : undefined}
      />
    );
  };

  const renderBlock = (block) => {
    if (block.kind === "choice") {
      const optional = !block.options.some((option) => option.field.required);
      const selected = block.options.find((option) => option.field.name === block.selectedName);
      return (
        <FormControl
          key={block.key}
          component="fieldset"
          disabled={disabled}
          sx={{ width: "100%" }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <FormLabel component="legend" sx={{ fontSize: 13, fontWeight: 600 }}>
              {block.legend}
            </FormLabel>
            {/* A "tick one only" set may legitimately end up with none ticked, which a radio group
                alone cannot express once a choice has been made. */}
            {optional && selected && (
              <Button
                size="small"
                sx={{ minWidth: 0, px: 0.75, fontSize: 11 }}
                disabled={disabled}
                onClick={() => onChange(applyCheckboxChange(fields, values, selected.field, false))}
              >
                Clear
              </Button>
            )}
          </Stack>
          <RadioGroup
            value={block.selectedName}
            onChange={(event) => {
              const picked = block.options.find(
                (option) => option.field.name === event.target.value
              );
              onChange(applyCheckboxChange(fields, values, picked.field, true));
            }}
          >
            {block.options.map((option) => (
              <FormControlLabel
                key={option.field.name}
                value={option.field.name}
                control={<Radio size="small" />}
                label={option.label}
                slotProps={{ typography: { fontSize: 14 } }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }

    if (block.kind === "section") {
      return (
        <Box key={block.key}>
          <Box sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{block.title}</Box>
          <Stack spacing={1.25} sx={{ pl: 1.5, borderLeft: "2px solid", borderColor: "divider" }}>
            {block.fields.map(({ field, label }) => (
              <Box key={field.name}>{renderControl(field, label)}</Box>
            ))}
          </Stack>
        </Box>
      );
    }

    return <Box key={block.key}>{renderControl(block.field, block.field.label)}</Box>;
  };

  return (
    <>
      <Stack spacing={2}>{blocks.map(renderBlock)}</Stack>

      <Dialog
        open={Boolean(signatureField)}
        onClose={() => setSignatureField("")}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Draw signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #cbd5e1", borderRadius: 2, overflow: "hidden", mt: 1 }}>
            <canvas
              ref={canvasRef}
              width={640}
              height={200}
              style={{ width: "100%", display: "block", touchAction: "none" }}
              onPointerDown={startDraw}
              onPointerMove={moveDraw}
              onPointerUp={() => {
                drawingRef.current = false;
              }}
              onPointerLeave={() => {
                drawingRef.current = false;
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const canvas = canvasRef.current;
              if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (signatureField && canvasRef.current) {
                setValue(signatureField, canvasRef.current.toDataURL("image/png"));
              }
              setSignatureField("");
            }}
          >
            Use signature
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const missingRequiredAdminFields = (fields, values, assetUrls = {}) =>
  (fields || [])
    // A hidden field belongs to a branch the form did not take, so it is never owed.
    .filter((field) => isFieldVisible(field, values))
    .filter((field) => field.required)
    // A signature already on file (the MARA agent's) is applied on completion, so it is not a gap
    // for the admin to fill even though nothing has been typed into it here.
    .filter((field) => !assetUrls?.[field.name])
    // Nor is a record-owned one that is missing: nobody can fill it from this form, so it is
    // reported as a blocker instead — see blockedRecordOwnedSignatures.
    .filter((field) => !field.recordOwned)
    .filter((field) => !String(values?.[field.name] || "").trim())
    .map((field) => field.label);

/**
 * Required signatures the linked MARA record owns but has nothing on file for. These stop the
 * document being completed and cannot be resolved here at all: the agent's signature is set in
 * their MARA record.
 */
export const blockedRecordOwnedSignatures = (fields, values, assetUrls = {}) =>
  (fields || [])
    .filter((field) => field.recordOwned)
    .filter((field) => field.required)
    .filter((field) => isFieldVisible(field, values))
    .filter((field) => !assetUrls?.[field.name])
    .map((field) => field.label);

export default AdminFillFieldsForm;
